import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useLanguage } from '../LanguageContext';
import { shouldShowLocalSocialLoginStubs } from './loginDemoVisibility';
import './Auth.css';

const showLocalSocialLoginStubs = shouldShowLocalSocialLoginStubs(process.env);

export default function Register() {
  const { register } = useAuth();
  const { language, setLanguage, copy } = useLanguage();
  const auth = copy.auth;
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from?.pathname
    ? `${location.state.from.pathname}${location.state.from.search || ''}`
    : '/';
  const [form, setForm] = useState({ username: '', email: '', password: '' });
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!consent) {
      setError(auth.consentRequired);
      return;
    }
    if (form.password.length < 8) {
      setError(auth.passwordLength);
      return;
    }
    setLoading(true);
    try {
      await register(form.username, form.email, form.password);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(err.code === 'ECONNABORTED' ? auth.registerTimeout : err.response?.data?.error || auth.registerFailed);
    } finally {
      setLoading(false);
    }
  };

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));
  const showNotice = (message) => {
    setError('');
    setNotice(message);
    window.setTimeout(() => setNotice(''), 2600);
  };

  const hero = language === 'zh' ? {
    title: '开始建立你的专业工作台',
    text: '创建账号后即可发布服务、接单、管理财务和消息，让协作过程保持在同一个平台里。',
    statOne: '立即创建',
    statOneLabel: '完整账号',
    statTwo: '快速开始',
    statTwoLabel: '平台内工作流',
    formTitle: '创建 Oblitrok 账号',
    formSubtitle: '填写基本信息并确认平台规则后继续。',
  } : {
    title: 'Start building your professional workspace',
    text: 'Create an account to publish services, accept work, manage finances, and keep collaboration in one platform.',
    statOne: 'Create now',
    statOneLabel: 'A complete account',
    statTwo: 'Quick start',
    statTwoLabel: 'A platform workflow',
    formTitle: 'Create your Oblitrok account',
    formSubtitle: 'Enter your details and confirm the platform rules to continue.',
  };

  return (
    <div className="auth-page career-auth-page">
      <header className="auth-topbar career-auth-topbar">
        <Link to="/" className="auth-brand">Oblitrok</Link>
        <div className="language-switch language-switch-light auth-language" aria-label={copy.nav.language}>
          <button type="button" className={language === 'en' ? 'active' : ''} onClick={() => setLanguage('en')}>EN</button>
          <button type="button" className={language === 'zh' ? 'active' : ''} onClick={() => setLanguage('zh')}>中文</button>
        </div>
      </header>
      <main className="career-auth-shell">
        <section className="career-auth-hero">
          <div className="career-hero-copy">
            <span className="career-hero-eyebrow">Oblitrok</span>
            <h1>{hero.title}</h1>
            <p>{hero.text}</p>
          </div>
          <div className="career-hero-stats">
            <div><strong>{hero.statOne}</strong><span>{hero.statOneLabel}</span></div>
            <div><strong>{hero.statTwo}</strong><span>{hero.statTwoLabel}</span></div>
          </div>
        </section>

        <section className="career-auth-panel">
          <div className="auth-card career-auth-card">
            <div className="career-card-title">
              <h2>{hero.formTitle}</h2>
              <p>{hero.formSubtitle}</p>
            </div>
            <form onSubmit={handleSubmit}>
              <label className="auth-field">
                <span className="auth-field-label">{auth.username}</span>
                <input autoComplete="username" value={form.username} onChange={set('username')} placeholder={auth.username} />
              </label>
              <label className="auth-field">
                <span className="auth-field-label">{auth.email}</span>
                <input type="email" autoComplete="email" value={form.email} onChange={set('email')} placeholder={auth.email} />
              </label>
              <label className="auth-field">
                <span className="auth-field-label">{auth.passwordHint}</span>
                <input type="password" autoComplete="new-password" value={form.password} onChange={set('password')} placeholder={auth.passwordHint} />
              </label>
              <label className="auth-consent">
                <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} />
                <span className="auth-consent-copy">
                  {auth.consentPrefix}{' '}
                  <Link to="/terms">{auth.termsLink}</Link>{' '}
                  {auth.consentJoiner}{' '}
                  <Link to="/privacy-policy">{auth.privacyLink}</Link>.
                </span>
              </label>
              {error && <p className="error-msg">{error}</p>}
              <button type="submit" className="auth-primary" disabled={loading}>
                {loading ? auth.registering : auth.createAccount}
              </button>
            </form>

            <div className="auth-divider"><span>{auth.or}</span></div>

            {notice && <p className="auth-inline-note">{notice}</p>}
            {showLocalSocialLoginStubs && (
              <>
                <button type="button" className="social-btn google-btn" onClick={() => showNotice(auth.googleUnavailable || 'Third-party signup is not connected yet.')}>
                  <span className="google-mark">G</span>
                  {auth.google}
                </button>
                <button type="button" className="social-btn apple-btn" onClick={() => showNotice(auth.appleUnavailable || 'Third-party signup is not connected yet.')}>
                  <span>Apple</span>
                  {auth.apple}
                </button>
              </>
            )}

            <div className="auth-card-bottom compact">
              <p>{auth.hasAccount}</p>
              <Link to="/login" className="auth-secondary">{auth.login}</Link>
            </div>
          </div>
        </section>
      </main>
      <footer className="auth-footer">
        <span>{auth.footer}</span>
      </footer>
    </div>
  );
}
